import React, { useState } from 'react';
import { X, Copy, Trash2 } from 'lucide-react';
import { useFunnelStore } from '../../store/funnelStore';
import ContentTab from '../Properties/ContentTab';
import StyleTab from '../Properties/StyleTab';
import LayoutTab from '../Properties/LayoutTab';
import ResponsiveTab from '../Properties/ResponsiveTab';
import AdvancedTab from '../Properties/AdvancedTab';

type TabType = 'content' | 'style' | 'layout' | 'responsive' | 'advanced';

/**
 * Properties Panel Component
 * Displays properties editor for the selected element
 */
const PropertiesPanel: React.FC = () => {
  const { selectedElementId, selectElement, deleteElement, duplicateElement, funnel } =
    useFunnelStore();
  const [activeTab, setActiveTab] = useState<TabType>('content');

  // Find the selected element (including nested elements in Column Layout)
  const findSelectedElement = () => {
    if (!selectedElementId) return null;

    for (const section of funnel.sections) {
      for (const column of section.columns) {
        // Check regular elements
        const element = column.elements.find((el) => el.id === selectedElementId);
        if (element) return element;

        // Check nested elements within Column Layout elements
        for (const el of column.elements) {
          if (el.type === 'columnLayout' && el.content?.columns) {
            for (const nestedCol of el.content.columns) {
              const nestedElement = nestedCol.elements?.find((ne: any) => ne.id === selectedElementId);
              if (nestedElement) return nestedElement;
            }
          }
        }
      }
    }
    return null;
  };

  const selectedElement = findSelectedElement();

  if (!selectedElement) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center">
        <div className="space-y-3">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <div className="w-8 h-8 rounded-lg bg-gray-300/50"></div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">No element selected</p>
            <p className="text-xs mt-1.5 text-gray-500">Click on an element to edit its properties</p>
          </div>
        </div>
      </div>
    );
  }

  const tabs: Array<{ id: TabType; label: string }> = [
    { id: 'content', label: 'Content' },
    { id: 'style', label: 'Style' },
    { id: 'layout', label: 'Layout' },
    { id: 'responsive', label: 'Responsive' },
    { id: 'advanced', label: 'Advanced' },
  ];

  const handleClose = () => {
    selectElement(null);
  };

  const handleDuplicate = () => {
    if (!selectedElementId) return;
    
    // Duplicate the element
    duplicateElement(selectedElementId);
    
    // After duplication, find and select the new element
    setTimeout(() => {
      const newState = useFunnelStore.getState();
      let newElementId: string | null = null;
      
      // Find the newly created element (it will be right after the current one)
      for (const section of newState.funnel.sections) {
        for (const column of section.columns) {
          const currentIndex = column.elements.findIndex(el => el.id === selectedElementId);
          if (currentIndex !== -1 && column.elements[currentIndex + 1]) {
            newElementId = column.elements[currentIndex + 1].id;
            break;
          }
        }
        if (newElementId) break;
      }
      
      if (newElementId) {
        selectElement(newElementId);
      }
    }, 100);
  };

  const handleDelete = () => {
    if (confirm('Delete this element?')) {
      deleteElement(selectedElementId!);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-gray-50/50">
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-200/80 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 animate-pulse"></div>
            <h2 className="text-sm font-semibold text-gray-800 tracking-wide">
              Properties
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="group relative p-1.5 -mr-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
            title="Close (Esc)"
          >
            <X size={18} className="transition-transform group-hover:rotate-90 duration-200" />
          </button>
        </div>

        {/* Element Type Badge & Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="px-3 py-1.5 bg-gradient-to-r from-primary-500/10 to-primary-600/10 border border-primary-200/50 text-primary-700 text-xs font-semibold rounded-lg backdrop-blur-sm shadow-sm">
            {selectedElement.type}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleDuplicate}
              className="group relative p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200 active:scale-95"
              title="Duplicate Element"
            >
              <Copy size={16} className="transition-transform group-hover:scale-110" />
            </button>
            <button
              onClick={handleDelete}
              className="group relative p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 active:scale-95"
              title="Delete Element"
            >
              <Trash2 size={16} className="transition-transform group-hover:scale-110" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative px-4 py-2 text-xs font-semibold rounded-lg transition-all duration-200 whitespace-nowrap
                ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-500/30 scale-105'
                    : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 hover:text-gray-800 active:scale-95'
                }
              `}
            >
              {activeTab === tab.id && (
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-400/20 to-primary-600/20 animate-pulse"></div>
              )}
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-5 scrollbar-modern">
        <div className="animate-fade-in">
          {activeTab === 'content' && <ContentTab element={selectedElement} />}
          {activeTab === 'style' && <StyleTab element={selectedElement} />}
          {activeTab === 'layout' && <LayoutTab element={selectedElement} />}
          {activeTab === 'responsive' && <ResponsiveTab element={selectedElement} />}
          {activeTab === 'advanced' && <AdvancedTab element={selectedElement} />}
        </div>
      </div>
    </div>
  );
};

export default PropertiesPanel;

